/**
 * Terms of Service page.
 *
 * DRAFT: this is a starting-point template, the same way the Privacy Policy
 * page is. It has not been reviewed by a lawyer — have it checked before
 * relying on it as your published terms.
 *
 * @module components/TermsOfService/TermsOfService
 */

import React from 'react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';

const LAST_UPDATED = 'September 27, 2026';

/**
 * TermsOfService component.
 * @function TermsOfService
 * @returns {string} Markup of the component.
 */
const TermsOfService = () => (
  <div className="view-padding">
    <Helmet>
      <title>Terms of Service</title>
    </Helmet>
    <article id="terms-of-service">
      <h1 className="content-title">Terms of Service</h1>
      <p>Last updated: {LAST_UPDATED}</p>

      <p>
        These Terms of Service ("Terms") govern your use of the Pro Series Tools
        LLC ("Pro Series Tools", "we", "us") website and your purchase of our
        products. By placing an order with us, you agree to these Terms.
      </p>

      <h2>Products</h2>
      <p>
        We make and sell handmade leatherworking tools. Because each tool is
        handmade, small variations in finish, color, and dimensions between
        individual tools are normal and not considered defects.
      </p>

      <h2>Orders and Pricing</h2>
      <p>
        All orders are subject to acceptance and availability. Prices are shown
        in US dollars and do not include shipping or applicable taxes unless
        stated otherwise. We reserve the right to correct pricing errors and to
        cancel an order affected by one, in which case we will refund any
        payment already made for that order.
      </p>

      <h2>Payment</h2>
      <p>
        Because each tool is handmade to order, payment is due once the tools on
        your order have been made and are ready to ship, not when you place the
        order. Payments are processed by Square; by paying, you also agree to{' '}
        <a
          href="https://squareup.com/us/en/legal/general/ua"
          target="_blank"
          rel="noopener noreferrer"
        >
          Square's terms of service
        </a>
        . A credit card processing fee applies when paying by credit card, as
        shown on your order before payment.
      </p>

      <h2>Shipping</h2>
      <p>
        Shipping costs and estimated delivery times are shown on your order.
        Risk of loss and title to the tools you order pass to you once we hand
        the order to the carrier. We are not responsible for delays caused by
        the carrier or by incorrect address information you provide.
      </p>

      <h2>Returns, Refunds, and Warranty</h2>
      <p>
        Every tool we make is covered by a lifetime warranty against defects in
        materials and workmanship, provided it has been used and cared for as
        intended. The warranty does not cover damage from misuse, accident, or
        normal wear.
      </p>
      <p>
        You may return any tool within 30 days of delivery. Return shipping is
        paid by the customer; once we receive the tool back in its original
        condition, we will issue a refund.
      </p>
      <p>
        We will also buy back any tool we made, at any time, at a price we
        negotiate with you on a case-by-case basis.
      </p>

      <h2>Intellectual Property</h2>
      <p>
        The Pro Series Tools name, logo, tool designs, and the photos and text
        on this website are our property or used with permission, and may not be
        copied or used without our written consent.
      </p>

      <h2>Limitation of Liability</h2>
      <p>
        To the extent permitted by law, our liability for any claim relating to
        a purchase is limited to the amount you paid for that order. We are not
        liable for indirect, incidental, or consequential damages arising from
        the use of our tools.
      </p>

      <h2>Governing Law</h2>
      <p>
        These Terms are governed by the laws of the State of New Mexico, USA,
        without regard to its conflict-of-laws rules.
      </p>

      <h2>Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. Changes will be posted on
        this page with an updated "Last updated" date. Orders placed before a
        change are governed by the Terms in effect at the time of that order.
      </p>

      <h2>Contact Us</h2>
      <p>
        Pro Series Tools LLC
        <br />
        36 Road 5243
        <br />
        Farmington, NM 87401
        <br />
        United States
        <br />
        <a href="tel:+15056327039">+1 (505) 632-7039</a>
        <br />
        <a href="mailto:info@proseriesleathertools.com">
          info@proseriesleathertools.com
        </a>
      </p>
    </article>
  </div>
);

export default TermsOfService;
